import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle } from 'lucide-react';
import { GiBirdClaw, GiFeather, GiDeer, GiWoodPile } from 'react-icons/gi';
import { MdAutoFixHigh } from 'react-icons/md';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ContactCTA } from '@/components/features/ContactCTA';

const TAXIDERMY_LOGO = '/taxidermy/logo.png';

const taxidermyServices = [
  {
    icon: <GiBirdClaw className="h-8 w-8" />,
    title: 'Bird Taxidermy',
    description: 'Expert preservation of native and exotic bird species.',
  },
  {
    icon: <GiDeer className="h-8 w-8" />,
    title: 'Small Mammal Taxidermy',
    description: 'Quality mounting and preservation of small mammals.',
  },
  {
    icon: <MdAutoFixHigh className="h-8 w-8" />,
    title: 'Restoration Work',
    description: 'Repair and restoration of existing taxidermy pieces.',
  },
  {
    icon: <GiWoodPile className="h-8 w-8" />,
    title: 'Custom Mounts & Displays',
    description: 'Bespoke mounting solutions and display cases.',
  },
  {
    icon: <GiFeather className="h-8 w-8" />,
    title: 'Consultation',
    description: 'Expert advice on preservation and care of specimens.',
  },
];

/**
 * Taxidermy services page
 */
export function Taxidermy() {
  return (
    <>
      {/* Page Header */}
      <section className="section-padding relative overflow-hidden bg-gradient-to-b from-primary/5 to-transparent">
        {/* Background Logo - Engraved Effect */}
        <div
          className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 lg:right-[10%]"
          aria-hidden="true"
        >
          {/* Outer glow / shadow for engraved effect */}
          <div className="relative">
            <img
              src={TAXIDERMY_LOGO}
              alt=""
              className="h-[350px] w-[350px] object-contain opacity-[0.08] blur-[1px] dark:invert lg:h-[450px] lg:w-[450px]"
            />
            {/* Inner highlight for depth */}
            <img
              src={TAXIDERMY_LOGO}
              alt=""
              className="absolute inset-0 h-[350px] w-[350px] translate-x-[2px] translate-y-[2px] object-contain opacity-[0.03] dark:invert lg:h-[450px] lg:w-[450px]"
            />
          </div>
        </div>

        <div className="container-custom relative">
          <div className="max-w-2xl">
            <span className="category-label">Specialty Services</span>
            <h1 className="text-4xl font-bold md:text-5xl">
              Taxidermy Services
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Expert taxidermy services in Tasmania. From birds to small mammals,
              I provide professional preservation with attention to detail and
              respect for the natural beauty of each specimen.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild size="lg" className="gap-2">
                <Link to="/contact">
                  Contact for Price
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/gallery">View Gallery</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding" aria-labelledby="taxidermy-services">
        <div className="container-custom">
          <div className="mb-12">
            <span className="category-label">What's Offered</span>
            <h2 id="taxidermy-services" className="text-3xl font-bold">
              Our Taxidermy Services
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {taxidermyServices.map((service, index) => (
              <Card key={index} className="card-hover">
                <CardContent className="pt-6">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-muted text-primary">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-semibold">{service.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview Section */}
      <section className="section-padding bg-card" aria-labelledby="gallery-preview">
        <div className="container-custom">
          <Card className="overflow-hidden rounded-2xl border-0 bg-muted/50">
            <div className="grid lg:grid-cols-5">
              {/* Left Content */}
              <div className="p-8 lg:col-span-3 lg:p-12">
                <span className="category-label">Portfolio</span>
                <h2 id="gallery-preview" className="mt-2 text-3xl font-bold">
                  View My Work
                </h2>
                <p className="mt-4 text-muted-foreground">
                  Browse my collection of completed taxidermy pieces in the gallery.
                  Each specimen is preserved with meticulous attention to detail,
                  capturing the natural beauty and character of the animal.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    'Birds and mammal specimens',
                    'Restoration and repair projects',
                    'Custom mounts and display pieces',
                    'Before and after transformations',
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-sm text-muted-foreground">
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <GiFeather className="h-3 w-3" aria-hidden="true" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <Button asChild className="mt-8 gap-2">
                  <Link to="/gallery">
                    Browse Gallery
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </Button>
              </div>

              {/* Right Content - Logo Display */}
              <div className="relative flex items-center justify-center border-t border-border/50 bg-gradient-to-br from-primary/5 to-primary/10 p-8 lg:col-span-2 lg:border-l lg:border-t-0 lg:p-12">
                <div className="relative">
                  {/* Engraved effect */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src={TAXIDERMY_LOGO}
                      alt=""
                      className="h-48 w-48 object-contain opacity-[0.06] blur-[0.5px] dark:invert lg:h-56 lg:w-56"
                      aria-hidden="true"
                    />
                  </div>
                  <img
                    src={TAXIDERMY_LOGO}
                    alt="Cloud 9 Taxidermy"
                    className="relative h-48 w-48 object-contain opacity-80 dark:invert lg:h-56 lg:w-56"
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Shipping Disclaimer */}
      <section className="section-padding" aria-labelledby="shipping-notice">
        <div className="container-custom">
          <Card className="border-destructive/20 bg-destructive/5">
            <CardContent className="p-6 md:p-8">
              <div className="flex gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
                  <AlertTriangle className="h-6 w-6" aria-hidden="true" />
                </div>
                <div>
                  <h2 id="shipping-notice" className="text-xl font-bold text-destructive">
                    Important: Shipping Disclaimer
                  </h2>
                  <div className="mt-4 space-y-4 text-sm text-muted-foreground">
                    <p>
                      <strong className="text-foreground">Interstate shipping of taxidermied specimens may require
                      permits under Australian wildlife protection laws.</strong>
                    </p>
                    <div>
                      <strong className="text-foreground">Species-specific requirements:</strong>
                      <ul className="mt-2 list-disc pl-5 space-y-1">
                        <li>
                          <strong>Native Australian birds</strong> (e.g., Kookaburra, Cockatoo):
                          May require state wildlife permits
                        </li>
                        <li>
                          <strong>CITES-listed species:</strong> International shipping prohibited
                          without CITES permits
                        </li>
                        <li>
                          <strong>Protected species:</strong> Cannot be shipped across state lines
                          without proper documentation
                        </li>
                      </ul>
                    </div>
                    <p>
                      It is the buyer's responsibility to verify permit requirements, obtain necessary
                      permits, and comply with all applicable wildlife regulations.
                    </p>
                    <Button asChild variant="outline" size="sm" className="mt-4">
                      <Link to="/legal/shipping">
                        Read Full Shipping Policy
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Process Overview */}
      <section className="relative overflow-hidden py-20 md:py-28 lg:py-36" aria-labelledby="process">
        {/* Engraved Background Logo with outline effect */}
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          aria-hidden="true"
        >
          {/* Outer shadow/glow for depth */}
          <div className="relative">
            <img
              src={TAXIDERMY_LOGO}
              alt=""
              className="h-[500px] w-[500px] object-contain opacity-[0.04] blur-[2px] dark:invert md:h-[600px] md:w-[600px]"
            />
            {/* Highlight layer offset for engraved look */}
            <img
              src={TAXIDERMY_LOGO}
              alt=""
              className="absolute inset-0 h-[500px] w-[500px] translate-x-[1px] translate-y-[1px] object-contain opacity-[0.02] dark:invert md:h-[600px] md:w-[600px]"
            />
            {/* Sharp center layer */}
            <img
              src={TAXIDERMY_LOGO}
              alt=""
              className="absolute inset-0 h-[500px] w-[500px] object-contain opacity-[0.015] dark:invert md:h-[600px] md:w-[600px]"
            />
          </div>
        </div>

        <div className="container-custom relative">
          <div className="mb-12 text-center">
            <span className="category-label">How It Works</span>
            <h2 id="process" className="text-3xl font-bold">
              The Process
            </h2>
          </div>

          <div className="grid gap-8 md:grid-cols-4">
            {[
              { step: '01', title: 'Consultation', desc: 'Discuss your requirements and preferences' },
              { step: '02', title: 'Assessment', desc: 'Evaluate the specimen and provide a quote' },
              { step: '03', title: 'Preservation', desc: 'Expert taxidermy work with attention to detail' },
              { step: '04', title: 'Collection', desc: 'Pick up or delivery of your finished piece' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-2xl font-bold text-primary">
                  {item.step}
                </div>
                <h3 className="font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <ContactCTA />
    </>
  );
}
